import React, { useState, useEffect } from "react";
import { login, loginWithPasswordlessAuth } from "../services/authService";
import { useNavigate, useLocation } from "react-router-dom";
import { getWebsiteUrl } from "../services/environmentService";
import { selectAuthenticated } from "../store/slices/authUserSlice";
import { useSelector } from "react-redux";

// TODO (jsani): load from server side — false sends all users to /onboarding on login
// const HAS_ONBOARDED = false;
const HAS_ONBOARDED = true;

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string>("");

  // either show a single email input for passwordless auth or the email/password combo input
  const [shouldUsePasswordlessAuth, setShouldUsePasswordlessAuth] =
    useState(true);
  const [message, setMessage] = useState<string>("");
  const [
    isPasswordlessAuthButtonDisabled,
    setIsPasswordlessAuthButtonDisabled,
  ] = useState(false);
  const isAuthenticated = useSelector(selectAuthenticated);

  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const status = searchParams.get('status');

  // Helper function to extract agency from domain or query param
  const parseAgency = () => {
    // First check query param
    const urlAgency = searchParams.get("agency");
    if (urlAgency) return urlAgency;

    // Then check domain
    const hostname = window.location.hostname;
    if (hostname.includes('unifiedmmg')) {
      return 'unifiedmmg';
    }
    if (hostname.includes('instantestimate')) {
      return 'instantestimate';
    }
    if (hostname.includes('authoritydigest')) {
      return 'authoritydigest';
    }
    if (hostname.includes('askspectra') || hostname.includes('spectratechconsultants')) {
      return 'spectra';
    }

    return null;
  };

  let errorMessage = "";
  if (status === "timeout") {
    errorMessage = "Session timed out. Please log in again.";
  }
  else if (status === "account_exists") {
    errorMessage = "Account already exists. Please log in.";
  } else if (status === "invalid_invite") {
    errorMessage = "Invalid invite. Please try again.";
  } else if (status === "accepted_invite") {
    errorMessage = "Invite already accepted. Please log in.";
  } else if (status === "invalid_link") {
    errorMessage = "That sign-in link is no longer valid. Enter your email below to get a new one.";
  }

  useEffect(() => {
    if (isAuthenticated) {
      if (HAS_ONBOARDED) {
        navigate("/dashboard");
      } else {
        navigate("/onboarding");
      }
    }
  }, [navigate, isAuthenticated]);

  const handleLoginError = (error: any) => {
    console.error("Login error:", error);

    if (error.status === 404) {
      setError(
        "We couldn't find an account for that email. If you think you should have a login, contact team@withpotions.com.",
      );
      return;
    }

    const errorMessage = error.response?.data?.error || error.message || "An unexpected error occurred";
    setError(errorMessage);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    const parsedAgency = parseAgency();
    const redirectPage = parsedAgency
      ? `/dashboard?agency=${parsedAgency}`
      : "/dashboard";

    const normalizedEmail = email.trim().toLowerCase();

    if (!shouldUsePasswordlessAuth) {
      login(normalizedEmail, password)
        .then((result: any) => {
          if (result?.mfa_required) {
            navigate("/mfa-verify");
            return;
          }
          navigate(redirectPage);
        })
        .catch(handleLoginError);
      return;
    }

    const redirectUrl = `${getWebsiteUrl()}${redirectPage}`;
    loginWithPasswordlessAuth({ email: normalizedEmail, redirectUrl })
      .then(() => {
        setError("");
        setMessage(
          "We've sent you an email! Click on the link in the email to continue!",
        );
        setIsPasswordlessAuthButtonDisabled(true);
        setTimeout(() => {
          setIsPasswordlessAuthButtonDisabled(false);
        }, 5000);
      })
      .catch(handleLoginError);
  };

  return (
    <form onSubmit={handleLogin}>
      <h1>Login</h1>
      {errorMessage && (
        <div style={{
          backgroundColor: "#FFEBEE",
          color: "#D32F2F",
          width: "39.7em",
          padding: "10px",
          marginBottom: "15px",
          borderRadius: "4px",
          border: "1px solid #FFCDD2"
        }}>
          {errorMessage}
        </div>
      )}
      <div>
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          className="primary-input"
          placeholder="Email"
          required
        />
      </div>
      {!shouldUsePasswordlessAuth ? (
        <div>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="primary-input"
            placeholder="Password"
            required
          />
        </div>
      ) : (
        <></>
      )}
      {error && <p style={{color: "red"}}>{error}</p>}
      {message && <p style={{color: "green"}}>{message}</p>}
      {shouldUsePasswordlessAuth ? (
        <p>
          We'll send you a magic link to sign in. You can also{" "}
          <a href="#" onClick={() => setShouldUsePasswordlessAuth(false)}>
            sign in with your password.
          </a>{" "}
          Don't have a login and think you should? Email us at{" "}
          <a href="mailto:team@withpotions.com">team@withpotions.com</a>.
        </p>
      ) : (
        <>
          <a href="#" onClick={() => setShouldUsePasswordlessAuth(true)}>
            <p>Get a magic link to sign in</p>
          </a>
        </>
      )}

      {shouldUsePasswordlessAuth ? (
        <button
          type="submit"
          className="primary-button"
          disabled={isPasswordlessAuthButtonDisabled}>
          {isPasswordlessAuthButtonDisabled
            ? "Send another email in 5 seconds..."
            : "Email me a link"}
        </button>
      ) : (
        <button type="submit" className="primary-button">
          Login
        </button>
      )}
    </form>
  );
};

export default Login;
